/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationStart, Route, Router } from '@angular/router';
import { Organization } from '@app/app/shared/models';
import { Select } from '@ngxs/store';
import { Observable, filter } from 'rxjs';
import { UserAuthState } from '../../../user-auth/store/user-auth.state';

@Injectable()
export class PageTitleService {
  @Select(UserAuthState.organizationDetails)
  private organizationData$: Observable<Organization>;
  private organizationName: string;
  private titles: string[] = [];
  private changes = 0;
  private changesAtNavigationStart = 0;
  private owner: { route: Route; depth: number } | null = null;

  constructor(
    private titleService: Title,
    router: Router,
  ) {
    const organizationDataSubscription = this.organizationData$.subscribe((data) => {
      if (data) {
        this.organizationName = data.name;
        this.refreshTitle();
        organizationDataSubscription.unsubscribe();
      }
    });

    router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.changesAtNavigationStart = this.changes;
    });
  }

  get changeCount(): number {
    return this.changes;
  }

  setTitle(newTitle: string, owner?: ActivatedRoute) {
    this.titles = [newTitle];
    this.changes++;
    this.owner = owner ? ownerOf(owner) : null;
    this.refreshTitle();
  }

  setOrganizationTitle() {
    this.titles = [];
    this.changes++;
    this.owner = null;
    this.refreshTitle();
  }

  appendTitle(appendedTitle: string, maxDepth: number) {
    if (maxDepth < 1) {
      maxDepth = 1;
    }

    if (this.titles.length >= maxDepth) {
      this.titles.length = maxDepth - 1;
    }

    this.titles.push(appendedTitle);
    this.changes++;
    this.owner = null;
    this.refreshTitle();
  }

  refreshTitle(newOrganizationName?: string) {
    if (newOrganizationName) {
      this.organizationName = newOrganizationName;
    }

    const newTitle = [this.organizationName, ...this.titles].filter((part) => !!part).join(' | ');

    if (newTitle) {
      this.titleService.setTitle(newTitle);
    }
  }

  keepsPageTitle(routes: Route[]): boolean {
    if (this.changes !== this.changesAtNavigationStart) {
      return true;
    }

    return !!this.owner && routes[this.owner.depth] === this.owner.route;
  }

  setNavigationTitle(title: string | null) {
    this.titles = title ? [title] : [];
    this.changes++;
    this.owner = null;
    this.refreshTitle();
  }
}

function ownerOf(route: ActivatedRoute): { route: Route; depth: number } | null {
  const { routeConfig, pathFromRoot } = route.snapshot;

  return routeConfig ? { route: routeConfig, depth: pathFromRoot.filter((level) => !!level.routeConfig).length - 1 } : null;
}
